import type { DataExportResultDto } from '../types/api/stats';

export interface ExportTable {
  columns: string[];
  /** One array per record, aligned with `columns`; missing values are ''. */
  rows: string[][];
}

export const RECORD_ID_COLUMN = 'RecId';

/**
 * Pivots data-export records (RecId + Name/Value pairs) into a table. Columns are RecId followed by
 * the union of variable names in first-seen order.
 */
export function buildExportTable(results: DataExportResultDto[]): ExportTable {
  const columns: string[] = [RECORD_ID_COLUMN];
  const columnIndex = new Map<string, number>([[RECORD_ID_COLUMN, 0]]);

  results.forEach((record) => {
    (record.Data ?? []).forEach((item) => {
      if (!columnIndex.has(item.Name)) {
        columnIndex.set(item.Name, columns.length);
        columns.push(item.Name);
      }
    });
  });

  const rows = results.map((record) => {
    const row: string[] = new Array(columns.length).fill('');
    row[0] = record.RecId ?? '';
    (record.Data ?? []).forEach((item) => {
      row[columnIndex.get(item.Name) as number] = item.Value ?? '';
    });
    return row;
  });

  return { columns, rows };
}

const quoteCell = (value: string, delimiter: string): string => {
  const needsQuotes = value.includes(delimiter) || /["\r\n]/.test(value);
  return needsQuotes ? `"${value.replace(/"/g, '""')}"` : value;
};

/** Delimited text with a header row. CSV: delimiter ',' + CRLF. Spreadsheet paste: delimiter '\t'. */
export function buildDelimitedText(table: ExportTable, delimiter: ',' | '\t', lineBreak = '\r\n'): string {
  return [table.columns, ...table.rows]
    .map((row) => row.map((cell) => quoteCell(cell, delimiter)).join(delimiter))
    .join(lineBreak);
}
